import { useState, FC, useEffect, useRef } from 'react';
import { DateRange, DayPicker } from 'react-day-picker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateField } from '@mui/x-date-pickers/DateField';
import { Icon } from 'common-components';
import { CalendarTodayIcon } from '../../uiLibraryImports';
import { DateRangeProps } from './dateRangeTypes';
import { useTranslationContext } from '../../contexts/translationContext';
import 'react-day-picker/dist/style.css';
import './dateRange.scss';

const DateRangeCalendar: FC<DateRangeProps> = (props: DateRangeProps) => {
    const { from, to, limitUntil, setFromAndTo } = props;
    const [range, setRange] = useState<DateRange | undefined>({ from, to });
    const [fromError, setFromError] = useState<boolean>(false);
    const [toError, setToError] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);
    const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
    const toRef = useRef<HTMLDivElement>(null);
    const fromRef = useRef<HTMLDivElement>(null);
    const { translate } = useTranslationContext();

    const toAndFrom = {
        from: {
            value: from,
            onChange: (newValue: any) => onChangeFrom(newValue),
            ref: fromRef
        },
        to: {
            value: to,
            onChange: (newValue: any) => onChangeTo(newValue),
            ref: toRef
        }
    };

    useEffect(() => {
        if (range?.from) setCurrentMonth(range.from);
    }, [range?.from]);

    useEffect(() => {
        if (range?.to) setCurrentMonth(range.to);
    }, [range?.to]);

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (!toRef.current?.contains(e.target) && toError) {
                setFromAndTo(from, range?.to);
                setToError(false);
                setShowError(true);
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [toError, range?.to]);

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (!fromRef.current?.contains(e.target) && fromError) {
                setFromAndTo(range?.from, to);
                setFromError(false);
                setShowError(true);
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [fromError, range?.from]);

    const onSetRange = (range?: DateRange) => {
        if (showError) setShowError(false);
        setRange(range);
        setFromAndTo(range?.from, range?.to);
    };

    const onChangeFrom = (from: Date) => {
        if (showError) setShowError(false);
        if (fromError) setFromError(false);
        setFromAndTo(from, range?.to);
        const fromDate = new Date(from);
        const toDate = new Date(range?.to as Date);
        const isFromDateValid = !isNaN(fromDate.getTime()) && (!limitUntil || fromDate < limitUntil);
        const isToDateValid = !isNaN(toDate.getTime());
        if (isFromDateValid && (!isToDateValid || (isToDateValid && fromDate < toDate))) {
            setRange(prev => ({ to: prev?.to, from }));
        } else {
            setFromError(true);
            setRange(prev => ({ to: prev?.to, from: limitUntil && fromDate > limitUntil ? limitUntil : prev?.to }));
        }
    };

    const onChangeTo = (to: Date) => {
        if (showError) setShowError(false);
        if (toError) setToError(false);
        setFromAndTo(range?.from, to);
        const toDate = new Date(to);
        const fromDate = new Date(range?.from as Date);
        const isToDateValid = !isNaN(toDate.getTime()) && (!limitUntil || toDate < limitUntil);
        const isFromDateValid = !isNaN(fromDate.getTime());
        if (isToDateValid && (!isFromDateValid || (isFromDateValid && fromDate < toDate))) {
            setRange(prev => ({ from: prev?.from, to }));
        } else {
            setToError(true);
            setRange(prev => ({ from: prev?.from, to: limitUntil && toDate > limitUntil ? limitUntil : prev?.from }));
        }
    };

    return (
        <div className="date-range-wrapper">
            <div className="date-range-title">
                <div className="date-range-title-bold">{translate('DateRange')}</div>
                <div>(yyy/mm/dd)</div>
            </div>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div className="date-range-inputs">
                    {Object.keys(toAndFrom).map((value, index) => (
                        <div className="date-range-input" key={index}>
                            <DateField
                                ref={toAndFrom[value].ref}
                                maxDate={limitUntil}
                                value={toAndFrom[value].value}
                                format="yyyy/MM/dd"
                                InputProps={{
                                    autoComplete: 'off',
                                    endAdornment: (
                                        <CalendarTodayIcon sx={{ color: '#626262', opacity: '0.5', height: '1rem' }} />
                                    ),
                                    sx: {
                                        color: '#626262',
                                        fontSize: '0.7rem',
                                        height: '2.5rem',
                                        '& fieldset': {
                                            border: '1px solid rgba(98, 98, 98, 0.25) !important'
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: '1px solid #A588D2 !important'
                                        }
                                    }
                                }}
                                onChange={(newValue: any) => toAndFrom[value].onChange(newValue)}
                            />
                        </div>
                    ))}
                </div>
            </LocalizationProvider>
            {showError && (
                <div className="date-range-error-wrapper">
                    <div className="date-range-error">
                        <CalendarTodayIcon sx={{ color: '#716596', marginRight: '0.3rem', height: '1rem' }} />
                        <div>{translate('InvalidRangeSelectedWeSetItToTheClosestValidDates')}</div>
                    </div>
                    <Icon onClick={() => setShowError(false)} name="cancel" className="date-range-close-error" />
                </div>
            )}
            <DayPicker
                id="test"
                mode="range"
                selected={range}
                onSelect={onSetRange}
                toDate={limitUntil}
                onMonthChange={setCurrentMonth}
                month={currentMonth}
            />
        </div>
    );
};

export default DateRangeCalendar;
